/**
 * O que se mexe no pixel 2.0 (SPEC-20261010-2133): desenhado por cima das imagens do Blender,
 * em pontos (âncoras) que o próprio Blender exporta. Coordenadas = pixels da imagem (escala 3).
 */

export type Pt = [number, number]
export type Ancora = { tipo: string; pontos: Pt[]; peq?: boolean; cor?: string }

type Estado = { bolhas: { t: number; x: number }[]; peixes: { x: number; y: number; v: number; c: string }[]; barras: number[] }
const estados = new WeakMap<Ancora, Estado>()
function estado(a: Ancora): Estado {
  let e = estados.get(a)
  if (!e) {
    e = {
      bolhas: Array.from({ length: 6 }, (_, i) => ({ t: i / 6, x: Math.random() })),
      peixes: [
        { x: 0.25, y: 0.35, v: 0.0016, c: '#ff8a3d' },
        { x: 0.7, y: 0.6, v: -0.0012, c: '#ffd34d' },
        { x: 0.5, y: 0.25, v: 0.001, c: '#ff5d73' },
      ],
      barras: Array.from({ length: 12 }, () => 0.3 + Math.random() * 0.7),
    }
    estados.set(a, e)
  }
  return e
}

const caixa = (p: Pt[]) => {
  const xs = p.map((q) => q[0])
  const ys = p.map((q) => q[1])
  return { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) }
}

function onda(x: number, t: number) {
  return 1.2 * Math.sin(x * 0.55 + t * 3.2) + 0.6 * Math.sin(x * 1.3 - t * 2.1)
}

function agua(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const r = caixa(a.pontos)
  const w = r.x1 - r.x0
  const h = r.y1 - r.y0
  const e = estado(a)
  const grad = g.createLinearGradient(0, r.y0, 0, r.y1)
  grad.addColorStop(0, 'rgba(120,205,255,.55)')
  grad.addColorStop(1, 'rgba(30,120,220,.55)')
  g.beginPath()
  g.moveTo(r.x0, r.y1)
  for (let x = 0; x <= w; x++) g.lineTo(r.x0 + x, r.y0 + onda(x, t))
  g.lineTo(r.x1, r.y1)
  g.closePath()
  g.fillStyle = grad
  g.fill()
  g.strokeStyle = 'rgba(235,250,255,.9)'
  g.lineWidth = 0.8
  g.beginPath()
  for (let x = 0; x <= w; x++) (x ? g.lineTo : g.moveTo).call(g, r.x0 + x, r.y0 + onda(x, t))
  g.stroke()
  for (let k = 0; k < 2; k++) {
    const f = (t * 0.45 + k * 0.5) % 1
    g.strokeStyle = `rgba(230,248,255,${0.7 * (1 - f)})`
    g.lineWidth = 0.7
    g.beginPath()
    g.ellipse((r.x0 + r.x1) / 2, r.y0 - 7.5, 1 + f * w * 0.42, 0.6 + f * 5.7, 0, 0, Math.PI * 2)
    g.stroke()
  }
  for (const b of e.bolhas) {
    b.t += 0.006
    if (b.t > 1) {
      b.t = 0
      b.x = Math.random()
    }
    g.fillStyle = `rgba(255,255,255,${0.85 * (1 - b.t * 0.6)})`
    g.beginPath()
    g.arc(r.x0 + 2 + b.x * (w - 4) + Math.sin(b.t * 9) * 0.8, r.y1 - b.t * h, 0.7 + b.t * 0.5, 0, Math.PI * 2)
    g.fill()
  }
}

function aquario(g: CanvasRenderingContext2D, a: Ancora, t: number, parado: boolean) {
  const r = caixa(a.pontos)
  const w = r.x1 - r.x0
  const h = r.y1 - r.y0
  const e = estado(a)
  g.strokeStyle = 'rgba(225,246,255,.85)'
  g.lineWidth = 1
  g.beginPath()
  for (let x = 0; x <= w; x += 2) (x ? g.lineTo : g.moveTo).call(g, r.x0 + x, r.y0 + 1 + 0.8 * Math.sin(x * 0.2 + t * 2.4))
  g.stroke()
  for (const f of e.peixes) {
    if (!parado) f.x += f.v
    if (f.x > 0.92 || f.x < 0.08) f.v *= -1
    const x = r.x0 + f.x * w
    const y = r.y0 + (f.y + Math.sin(t * 1.5 + f.x * 9) * 0.04) * h
    const dir = f.v > 0 ? 1 : -1
    g.fillStyle = f.c
    g.fillRect(x - 4, y - 2, 8, 4)
    g.fillRect(x - 4 * dir - (dir > 0 ? 3 : 0), y - 3, 3, 6)
    g.fillStyle = '#1a1f33'
    g.fillRect(x + 2 * dir - (dir > 0 ? 0 : 1), y - 1, 1.5, 1.5)
  }
  for (const b of e.bolhas) {
    b.t += 0.004
    if (b.t > 1) {
      b.t = 0
      b.x = Math.random()
    }
    g.fillStyle = `rgba(255,255,255,${0.7 * (1 - b.t)})`
    g.beginPath()
    g.arc(r.x0 + 4 + b.x * (w - 8), r.y1 - 2 - b.t * (h - 4), 1, 0, Math.PI * 2)
    g.fill()
  }
}

function abajur(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const [c, chao] = a.pontos as [Pt, Pt]
  const k = a.peq ? 0.45 : 1
  const resp = 0.85 + 0.15 * Math.sin(t * 1.6) + 0.04 * Math.sin(t * 7.3)
  g.save()
  g.globalCompositeOperation = 'lighter'
  let gr = g.createRadialGradient(c[0], c[1], 2, c[0], c[1], 46 * k)
  gr.addColorStop(0, `rgba(255,214,140,${0.8 * resp})`)
  gr.addColorStop(0.4, `rgba(255,170,80,${0.3 * resp})`)
  gr.addColorStop(1, 'rgba(255,150,60,0)')
  g.fillStyle = gr
  g.fillRect(c[0] - 50 * k, c[1] - 50 * k, 100 * k, 100 * k)
  gr = g.createRadialGradient(chao[0], chao[1], 4, chao[0], chao[1], 70 * k)
  gr.addColorStop(0, `rgba(255,190,110,${0.32 * resp})`)
  gr.addColorStop(1, 'rgba(255,170,80,0)')
  g.fillStyle = gr
  g.beginPath()
  g.ellipse(chao[0], chao[1], 75 * k, 40 * k, 0, 0, Math.PI * 2)
  g.fill()
  for (let i = 0; i < 6 * k; i++) {
    const f = (t * 0.08 + i / 6) % 1
    g.fillStyle = `rgba(255,236,190,${0.6 * Math.sin(f * Math.PI)})`
    g.fillRect(c[0] - 18 * k + ((i * 13) % 36) * k + Math.sin(t + i) * 3, c[1] + 6 + f * 40 * k, 1, 1)
  }
  g.restore()
}

function tela(g: CanvasRenderingContext2D, a: Ancora, t: number, q: number) {
  const r = caixa(a.pontos)
  const w = r.x1 - r.x0
  const h = r.y1 - r.y0
  const e = estado(a)
  g.fillStyle = '#121833'
  g.fillRect(r.x0, r.y0, w, h)
  if (a.peq || w < 30) {
    for (let i = 0; i < 3; i++) {
      const l = ((Math.floor(t * 2) + i * 3) % 7) / 7
      g.fillStyle = ['#7fd4ff', '#b6f08a', '#ffd34d'][i] as string
      g.fillRect(r.x0 + 1.5, r.y0 + 1.5 + i * Math.max(1.8, h / 4), 2 + l * (w - 4), 1)
    }
  } else {
    g.fillStyle = '#2f4fd8'
    g.fillRect(r.x0 + 2, r.y0 + 2, w - 4, 2)
    if (q % 40 === 0) e.barras = e.barras.map((b) => Math.min(1, Math.max(0.15, b + (Math.random() - 0.5) * 0.5)))
    const cores = ['#6b91f0', '#ffb020', '#7fd4ff', '#b6f08a']
    const bw = (w * 0.55) / 12
    e.barras.forEach((b, i) => {
      const bh = (h - 10) * b
      g.fillStyle = cores[i % 4] as string
      g.fillRect(r.x0 + 4 + i * bw, r.y1 - 3 - bh, bw - 1.5, bh)
    })
    g.strokeStyle = '#e0679b'
    g.lineWidth = 1
    g.beginPath()
    for (let x = 0; x < w * 0.36; x += 2) (x ? g.lineTo : g.moveTo).call(g, r.x0 + w * 0.6 + x, r.y0 + 8 + Math.min(5, h / 4) * Math.sin(x * 0.25 + t * 2))
    g.stroke()
  }
  g.save()
  g.globalCompositeOperation = 'lighter'
  g.fillStyle = 'rgba(120,160,255,.08)'
  g.fillRect(r.x0, r.y0, w, h)
  g.restore()
}

/** Pingue-pongue: bolinha indo e voltando, quicando uma vez em cada lado, e as duas raquetes rebatendo. */
function pingue(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const [esq, dir, fundo, frente] = a.pontos as [Pt, Pt, Pt, Pt]
  const ciclo = 2.2
  const f = (t % ciclo) / ciclo
  const ida = f < 0.5
  const u = ida ? f * 2 : 2 - f * 2 // 0 = raquete da esquerda, 1 = da direita
  // jogada varia de lado a cada rebatida
  const jog = Math.floor(t / (ciclo / 2))
  const lado = (n: number) => 0.25 + 0.5 * ((Math.sin(n * 12.9898) * 43758.5453) % 1 + 1) % 1
  const de = lado(jog)
  const para = lado(jog + 1)
  const lat = ida ? de + (para - de) * (f * 2) : de + (para - de) * (f * 2 - 1)
  const base = (s: number, l: number): Pt => {
    const xa = esq[0] + (dir[0] - esq[0]) * s
    const ya = esq[1] + (dir[1] - esq[1]) * s
    return [xa + (frente[0] - fundo[0]) * (l - 0.5) * 0.8, ya + (frente[1] - fundo[1]) * (l - 0.5) * 0.8]
  }
  const [x, y] = base(u, lat)
  // dois arcos por travessia: sai da raquete, quica no lado do outro e chega na raquete
  const s = ida ? f * 2 : f * 2 - 1
  const alto = s < 0.55 ? Math.sin((s / 0.55) * Math.PI) * 16 : Math.sin(((s - 0.55) / 0.45) * Math.PI) * 9
  g.fillStyle = 'rgba(10,40,20,.35)'
  g.beginPath()
  g.ellipse(x, y, 4.6, 2.4, 0, 0, Math.PI * 2)
  g.fill()
  g.fillStyle = '#ffffff'
  g.beginPath()
  g.arc(x, y - alto * 1.4, 3.6, 0, Math.PI * 2)
  g.fill()
  g.strokeStyle = 'rgba(0,0,0,.35)'
  g.lineWidth = 0.6
  g.stroke()
  // raquetes: acompanham o lado da bolinha e dão o golpe quando ela chega
  const raquete = (fim: 0 | 1, cor: string, cabo: number) => {
    const chegando = (fim === 1 && ida) || (fim === 0 && !ida)
    const alvo = chegando ? lat : 0.5 + (lat - 0.5) * 0.4
    const [rx, ry] = base(fim, alvo)
    const perto = chegando ? Math.max(0, 1 - Math.abs(s - 1) * 6) : Math.max(0, 1 - s * 6)
    const golpe = Math.sin(perto * Math.PI) * 6
    const off = fim === 0 ? -12 - golpe * 1.5 : 12 + golpe * 1.5
    g.fillStyle = 'rgba(10,30,15,.3)'
    g.beginPath()
    g.ellipse(rx + off, ry + 4, 9, 3.5, 0, 0, Math.PI * 2)
    g.fill()
    g.save()
    g.translate(rx + off, ry - 12)
    g.scale(1.5, 1.5)
    g.rotate((fim === 0 ? -1 : 1) * (0.35 - golpe * 0.06))
    g.fillStyle = '#c9935a'
    g.fillRect(-1.5 + cabo, -1, 3, 9)
    g.fillStyle = cor
    g.beginPath()
    g.ellipse(0, -4, 5.5, 6, 0, 0, Math.PI * 2)
    g.fill()
    g.strokeStyle = 'rgba(0,0,0,.45)'
    g.lineWidth = 0.8
    g.stroke()
    g.restore()
  }
  raquete(0, '#e5484d', -1)
  raquete(1, '#2a2f44', 1)
}

function vapor(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const [p] = a.pontos as [Pt]
  for (let k = 0; k < 3; k++) {
    const f = (t * 0.35 + k / 3 + p[0] * 0.01) % 1
    g.strokeStyle = `rgba(255,255,255,${0.55 * Math.sin(f * Math.PI)})`
    g.lineWidth = 1.1
    g.beginPath()
    for (let i = 0; i < 6; i++) (i ? g.lineTo : g.moveTo).call(g, p[0] + Math.sin(f * 6 + i * 0.7 + k) * 1.6, p[1] - f * 16 - i * 1.6)
    g.stroke()
  }
}

function elevador(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const r = caixa(a.pontos)
  const andar = Math.floor(t / 3) % 9
  g.fillStyle = '#ffb020'
  g.font = `bold ${Math.round((r.y1 - r.y0) * 0.75)}px monospace`
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.fillText(String(andar === 0 ? 'T' : andar), (r.x0 + r.x1) / 2, (r.y0 + r.y1) / 2 + 1)
  const sobe = Math.floor(t / 3) % 2
  g.fillStyle = Math.floor(t * 2) % 2 ? '#7fd4ff' : '#2f4fd8'
  g.fillRect(r.x1 - 6, sobe ? r.y0 + 2 : r.y1 - 4, 3, 2)
}


/** Lareira: línguas de fogo tremendo entre as duas pontas, mais a luz quente no chão. */
function fogo(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const [e, d, topo] = a.pontos as [Pt, Pt, Pt]
  const alt = Math.max(8, e[1] - topo[1])
  const n = 7
  g.save()
  g.globalCompositeOperation = 'lighter'
  const brilho = g.createRadialGradient((e[0] + d[0]) / 2, e[1], 2, (e[0] + d[0]) / 2, e[1], (d[0] - e[0]) * 1.1)
  brilho.addColorStop(0, `rgba(255,150,60,${0.35 + 0.08 * Math.sin(t * 9)})`)
  brilho.addColorStop(1, 'rgba(255,120,40,0)')
  g.fillStyle = brilho
  g.fillRect(e[0] - 40, e[1] - alt * 1.4, d[0] - e[0] + 80, alt * 2.2)
  for (let i = 0; i < n; i++) {
    const f = i / (n - 1)
    const x = e[0] + (d[0] - e[0]) * (0.12 + f * 0.76)
    const h = alt * (0.45 + 0.4 * Math.sin(Math.PI * f)) * (0.75 + 0.25 * Math.sin(t * 11 + i * 2.3) + 0.12 * Math.sin(t * 23 + i))
    const w = ((d[0] - e[0]) / n) * 0.9
    const balanco = Math.sin(t * 6 + i) * 2
    for (const [k, cor] of [
      [1, 'rgba(255,90,30,.75)'],
      [0.65, 'rgba(255,170,40,.85)'],
      [0.32, 'rgba(255,240,180,.9)'],
    ] as const) {
      g.fillStyle = cor
      g.beginPath()
      g.moveTo(x - w * k, e[1])
      g.quadraticCurveTo(x - w * k * 0.6, e[1] - h * k * 0.6, x + balanco * k, e[1] - h * k)
      g.quadraticCurveTo(x + w * k * 0.6, e[1] - h * k * 0.6, x + w * k, e[1])
      g.closePath()
      g.fill()
    }
  }
  for (let i = 0; i < 5; i++) {
    const f = (t * 0.6 + i / 5) % 1
    g.fillStyle = `rgba(255,200,120,${0.8 * (1 - f)})`
    g.fillRect(e[0] + ((i * 37) % 100) / 100 * (d[0] - e[0]) + Math.sin(t * 3 + i) * 3, e[1] - f * alt * 1.6, 1.5, 1.5)
  }
  g.restore()
}

/** Fliperama: um joguinho de naves na tela. */
function fliper(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const r = caixa(a.pontos)
  const w = r.x1 - r.x0
  const h = r.y1 - r.y0
  g.save()
  g.beginPath()
  g.rect(r.x0, r.y0, w, h)
  g.clip()
  g.fillStyle = '#0b0e24'
  g.fillRect(r.x0, r.y0, w, h)
  for (let i = 0; i < 8; i++) {
    g.fillStyle = 'rgba(255,255,255,.5)'
    g.fillRect(r.x0 + ((i * 53 + t * 6) % w), r.y0 + ((i * 31 + t * 14) % h), 1, 1)
  }
  const passo = Math.floor(t * 2)
  const dx = ((passo % 8) < 4 ? passo % 4 : 4 - (passo % 4)) * (w * 0.04)
  const cores = ['#ff5d73', '#ffd34d', '#7fd4ff']
  const tam = Math.max(2, w * 0.07)
  for (let l = 0; l < 3; l++)
    for (let c = 0; c < 5; c++) {
      if ((l * 5 + c + Math.floor(t / 4)) % 7 === 0) continue
      const x = r.x0 + w * 0.14 + c * w * 0.15 + dx
      const y = r.y0 + h * 0.12 + l * h * 0.13
      g.fillStyle = cores[l] as string
      g.fillRect(x, y, tam, tam * 0.7)
      if (passo % 2) g.fillRect(x - 1, y + tam * 0.7, 1, 1.5)
      else g.fillRect(x + tam, y + tam * 0.7, 1, 1.5)
    }
  const nx = r.x0 + w * (0.5 + 0.35 * Math.sin(t * 1.3))
  g.fillStyle = '#b6f08a'
  g.fillRect(nx - tam * 0.7, r.y1 - h * 0.12, tam * 1.4, tam * 0.6)
  g.fillRect(nx - 0.6, r.y1 - h * 0.12 - tam * 0.4, 1.2, tam * 0.4)
  const tiro = (t * 1.6) % 1
  g.fillStyle = '#ffffff'
  g.fillRect(nx - 0.5, r.y1 - h * 0.14 - tiro * h * 0.6, 1, 2.5)
  g.fillStyle = 'rgba(255,255,255,.06)'
  for (let y = r.y0; y < r.y1; y += 2) g.fillRect(r.x0, y, w, 1)
  g.restore()
}

/** Letreiro neon: brilho que respira e pisca de vez em quando. */
function neon(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const r = caixa(a.pontos)
  const cor = a.cor ?? '#e0679b'
  const pisca = Math.sin(t * 0.7) > 0.97 && Math.sin(t * 40) > 0 ? 0.3 : 1
  const k = (0.55 + 0.2 * Math.sin(t * 2.2)) * pisca
  const n = parseInt(cor.slice(1), 16)
  const rgb = `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`
  g.save()
  g.globalCompositeOperation = 'lighter'
  const cx = (r.x0 + r.x1) / 2
  const cy = (r.y0 + r.y1) / 2
  const raio = (r.x1 - r.x0) * 0.75
  const gr = g.createRadialGradient(cx, cy, 4, cx, cy, raio)
  gr.addColorStop(0, `rgba(${rgb},${0.38 * k})`)
  gr.addColorStop(1, `rgba(${rgb},0)`)
  g.fillStyle = gr
  g.beginPath()
  g.ellipse(cx, cy + (r.y1 - r.y0) * 0.3, raio, raio * 0.7, 0, 0, Math.PI * 2)
  g.fill()
  g.restore()
}

/** Relógio de pé: ponteiros na hora certa e o pêndulo indo e voltando. */
function relogio(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const [c, piv, fim] = a.pontos as [Pt, Pt, Pt]
  const ry = Math.max(3, (piv[1] - c[1]) * 0.4)
  const rx = ry * 1.86
  const agora = new Date()
  const min = agora.getMinutes() + agora.getSeconds() / 60
  const hor = (agora.getHours() % 12) + min / 60
  const mao = (ang: number, k: number, lw: number, cor: string) => {
    g.strokeStyle = cor
    g.lineWidth = lw
    g.beginPath()
    g.moveTo(c[0], c[1])
    g.lineTo(c[0] + Math.sin(ang) * rx * k, c[1] - Math.cos(ang) * ry * k)
    g.stroke()
  }
  mao((hor / 12) * Math.PI * 2, 0.5, 1.6, '#2a1a10')
  mao((min / 60) * Math.PI * 2, 0.8, 1.1, '#2a1a10')
  mao(((agora.getSeconds() + (t % 1)) / 60) * Math.PI * 2, 0.85, 0.5, '#c2484f')
  const ang = Math.sin(t * Math.PI) * 0.22
  const L = fim[1] - piv[1]
  const bx = piv[0] + Math.sin(ang) * L * 0.9
  const by = piv[1] + Math.cos(ang) * L
  g.strokeStyle = '#c9a24a'
  g.lineWidth = 1
  g.beginPath()
  g.moveTo(piv[0], piv[1])
  g.lineTo(bx, by)
  g.stroke()
  g.fillStyle = '#e3bd5a'
  g.beginPath()
  g.ellipse(bx, by, rx * 0.35, ry * 0.5, 0, 0, Math.PI * 2)
  g.fill()
}

function led(g: CanvasRenderingContext2D, a: Ancora, t: number) {
  const [p] = a.pontos as [Pt]
  if (Math.floor(t * 1.5) % 3 === 0) return
  g.fillStyle = 'rgba(127,212,255,.9)'
  g.fillRect(p[0] - 1.5, p[1] - 1, 3, 2)
}

export function desenharAnimacoes(g: CanvasRenderingContext2D, ancoras: { a: Ancora; dx: number; dy: number }[], t: number, q: number, parado: boolean) {
  for (const { a, dx, dy } of ancoras) {
    g.save()
    g.translate(dx, dy)
    switch (a.tipo) {
      case 'agua':
        agua(g, a, t)
        break
      case 'aquario':
        aquario(g, a, t, parado)
        break
      case 'abajur':
        abajur(g, a, t)
        break
      case 'tela':
        tela(g, a, t, q)
        break
      case 'pingue':
        pingue(g, a, t)
        break
      case 'vapor':
        vapor(g, a, t)
        break
      case 'elevador':
        elevador(g, a, t)
        break
      case 'fogo':
        fogo(g, a, t)
        break
      case 'fliper':
        fliper(g, a, t)
        break
      case 'neon':
        neon(g, a, t)
        break
      case 'relogio':
        relogio(g, a, t)
        break
      case 'led':
        led(g, a, t)
        break
    }
    g.restore()
  }
}
